///
/// EarthBrowser
///
/// Created by Matt Giger on 4/14/2024
/// Copyright (c) 2024 Geodata Labs
///

import { logger } from "@/utils/LogDispatcher"

const normalTiff		= 0x002A
const bigTiff			= 0x002B

export class Geotiff {
	private version: number = 0
	public entries: GeotiffImageEntry[] = []

	constructor(fileBuffer: ArrayBuffer) {
		if(!fileBuffer) {
			throw new Error("File buffer not set")
		}
		const view = new DataView(fileBuffer)

		const lendian = view.getUint16(0) === 0x4949
		this.version = view.getUint16(2, lendian)
		if (this.version !== normalTiff && this.version !== bigTiff) {
			throw new Error("Invalid TIFF version")
		}

		let ifdOffset = this.version === normalTiff ? view.getUint32(4, lendian) : Number(view.getBigUint64(8, lendian))
		do {
			const ifd = new GeotiffImageEntry(view, lendian, this.version === bigTiff, ifdOffset)
			this.entries.push(ifd)
			ifdOffset = ifd.next
		} while(ifdOffset)
	}
}

export class GeotiffImageEntry {
	public entries: IFDEntry[] = []
    public metadata: Record<string, string | number> = {}
	public next: number
	private view: DataView
	private lendian: boolean
	public width: number = 0
	public height: number = 0
	public bitsPerSample: number = 0
	public samplesPerPixel: number = 0

	constructor(view: DataView, lendian: boolean, bigtiff: boolean, offset: number) {
		this.view = view
		this.lendian = lendian
		const count = view.getUint16(offset, lendian)
		for (let i = 0; i < count; i++) {
			const entry = IFDEntry.entry(view, lendian, bigtiff, offset + 2 + i * 12)
			this.entries.push(entry)
		}
		this.next = bigtiff ?
			Number(view.getBigUint64(offset + 2 + count * 12, lendian)) :
			view.getUint32(offset + 2 + count * 12, lendian)

		// Extract metadata from GdalMetadata entries
		const gdalMetadata = this.entries.find(e => e.tag === IFDTag.GdalMetadata) as GdalMetadata
		if (gdalMetadata) {
			gdalMetadata.extract(this.view, this.lendian)

			// Parse XML metadata if available
			// This is a simplified approach - a full XML parser would be more robust
			const metadataStr = gdalMetadata.metadata
			const itemRegex = /<Item name="([^"]+)">([^<]+)<\/Item>/g
			let match
			while ((match = itemRegex.exec(metadataStr)) !== null) {
				const key = match[1]
				const value = match[2]
				this.metadata[key] = value
			}
		}

		const earthBrowserMetadata = this.entries.find(e => e.tag === IFDTag.EarthBrowserMetadata) as EarthBrowserMetadata
		if (earthBrowserMetadata) {
			earthBrowserMetadata.extract(this.view, this.lendian)
		}

		// Extract metadata from other tags
		for (const entry of this.entries) {
			if (entry instanceof GdalNoData) {
				this.metadata.NODATA_VALUE = entry.nodata
			}
		}

		// Extract image properties
		const imageWidth = this.entries.find(e => e.tag === IFDTag.ImageWidth) as ImageWidth
		const imageHeight = this.entries.find(e => e.tag === IFDTag.ImageLength) as ImageHeight
		const bitsPerSample = this.entries.find(e => e.tag === IFDTag.BitsPerSample) as BitsPerSample
		const samplesPerPixel = this.entries.find(e => e.tag === IFDTag.SamplesPerPixel) as SamplesPerPixel

		if(imageWidth && imageHeight && bitsPerSample && samplesPerPixel) {
			this.width = imageWidth.value
			this.height = imageHeight.value
			this.bitsPerSample = bitsPerSample.value
			this.samplesPerPixel = samplesPerPixel.value
		}
	}

	public get valid(): boolean {
		return this.width > 0 && this.height > 0 && this.bitsPerSample > 0 && this.samplesPerPixel > 0
	}

	public readImageData(): Uint8Array {
		// Get required entries
		const stripOffsets = this.entries.find(e => e.tag === IFDTag.StripOffsets) as StripOffsets
		const stripByteCounts = this.entries.find(e => e.tag === IFDTag.StripByteCounts) as StripByteCounts

		if (!stripOffsets || !stripByteCounts) {
			throw new Error("Missing required TIFF tags")
		}
		stripOffsets.extract(this.view, this.lendian)
		stripByteCounts.extract(this.view, this.lendian)

		if(stripOffsets.offsets.length !== stripByteCounts.counts.length) {
			throw new Error("Strip offsets and strip byte counts lengths do not match")
		}

		// Calculate total size and create buffer
		const totalSize = stripByteCounts.counts.reduce((acc, count) => acc + count, 0)
		const imageData = new Uint8Array(totalSize)

		// Create a single view of the source buffer
		const sourceView = new Uint8Array(this.view.buffer, this.view.byteOffset)

		// Read each strip using subarray for more efficient memory usage
		let destOffset = 0
		const count = stripOffsets.offsets.length
		for (let i = 0; i < count; i++) {
			const start = stripOffsets.offsets[i]
			const size = stripByteCounts.counts[i]

			// Use subarray to get a view into the source data without creating a new array
			const sourceSlice = sourceView.subarray(start, start + size)

			// Use subarray to get a view into the destination at the correct offset
			const destSlice = imageData.subarray(destOffset, destOffset + size)

			// Copy the data (this modifies the original imageData buffer)
			destSlice.set(sourceSlice)
			destOffset += size
		}

		return imageData
	}
}

class IFDEntry {
	public static entry(view: DataView, lendian: boolean, bigtiff: boolean, offset: number): IFDEntry {
		const tag = view.getUint16(offset, lendian)
		const type = view.getUint16(offset + 2, lendian)
		const count = bigtiff ? Number(view.getBigUint64(offset + 4, lendian)) : view.getUint32(offset + 4, lendian)
		const value = bigtiff ? Number(view.getBigUint64(offset + 12, lendian)) : view.getUint32(offset + 8, lendian)

		switch(tag) {
			case IFDTag.ImageWidth:
				return new ImageWidth(tag, type, count, value)
			case IFDTag.ImageLength:
				return new ImageHeight(tag, type, count, value)
			case IFDTag.BitsPerSample:
				return new BitsPerSample(tag, type, count, value)
			case IFDTag.Compression:
				return new Compression(tag, type, count, value)
			case IFDTag.PhotometricInterpretation:
				return new PhotometricInterpretation(tag, type, count, value)
			case IFDTag.StripOffsets:
				return new StripOffsets(tag, type, count, value)
			case IFDTag.StripByteCounts:
				return new StripByteCounts(tag, type, count, value)
			case IFDTag.SamplesPerPixel:
				return new SamplesPerPixel(tag, type, count, value)
			case IFDTag.RowsPerStrip:
				return new RowsPerStrip(tag, type, count, value)
			case IFDTag.ModelPixelScale:
				return new ModelPixelScale(tag, type, count, value)
			case IFDTag.ModelTiePoint:
				return new ModelTiePoint(tag, type, count, value)
			case IFDTag.GdalNoData:
				return new GdalNoData(tag, type, count, value)
			case IFDTag.GdalMetadata:
				return new GdalMetadata(tag, type, count, value)
			case IFDTag.EarthBrowserMetadata:
				return new EarthBrowserMetadata(tag, type, count, value)
			default:
				return new IFDEntry(tag, type, count, value)
		}
	}

	constructor(public tag: IFDTag, public type: IFDType, public count: number, public value: number) {}

	public extract(_view: DataView, _lendian: boolean): void {
		// override in subclass
	}

	public get isOffset(): boolean {
		return this.count * ifdTypeSize(this.type) > 4
	}

	public rangeOffset(): [number, number] {
		return [this.value, this.value + this.count * ifdTypeSize(this.type)]
	}
}

class ImageWidth extends IFDEntry {}
class ImageHeight extends IFDEntry {}
class BitsPerSample extends IFDEntry {}
class Compression extends IFDEntry {}
class SamplesPerPixel extends IFDEntry {}
class RowsPerStrip extends IFDEntry {}
class PhotometricInterpretation extends IFDEntry {
	public get blackIsZero(): boolean {
		return this.value === 1
	}
}
class StripOffsets extends IFDEntry {
	public offsets: number[] = []

	override extract(view: DataView, lendian: boolean): void {
		for (let i = 0; i < this.count; i++) {
			this.offsets.push(view.getUint32(this.value + i * 4, lendian))
		}
	}
}
class StripByteCounts extends IFDEntry {
	public counts: number[] = []

	override extract(view: DataView, lendian: boolean): void {
		for (let i = 0; i < this.count; i++) {
			this.counts.push(view.getUint16(this.value + i * 2, lendian))
		}
	}
}
class ModelPixelScale extends IFDEntry {
	public x: number = 0
	public y: number = 0
	public z: number = 0
	override extract(view: DataView, lendian: boolean): void {
		this.x = view.getFloat64(this.value, lendian)
		this.y = view.getFloat64(this.value + 8, lendian)
		this.z = view.getFloat64(this.value + 16, lendian)
	}
}
class ModelTiePoint extends IFDEntry {
	public points: number[] = []
	override extract(view: DataView, lendian: boolean): void {
		for (let i = 0; i < this.count; i++) {
			this.points.push(view.getFloat64(this.value + i * 8, lendian))
		}
	}
}
class GdalNoData extends IFDEntry {
	public nodata: number = 0

	override extract(view: DataView, _lendian: boolean): void {
		logger.assert(this.type == IFDType.ASCII, "Invalid type")

		let str = ""
		for(let i = 0; i < this.count; i++) {
			str += String.fromCharCode(view.getUint8(this.value + i))
		}
		this.nodata = parseInt(str)
	}
}
class GdalMetadata extends IFDEntry {
	public metadata: string = ""

	override extract(view: DataView, _lendian: boolean): void {
		logger.assert(this.type == IFDType.ASCII, "Invalid type")

		let str = ""
		for(let i = 0; i < this.count; i++) {
			str += String.fromCharCode(view.getUint8(this.value + i))
		}
		this.metadata = str
	}
}
class EarthBrowserMetadata extends IFDEntry {
	public metadata: object = {}

	override extract(view: DataView, _lendian: boolean): void {
		logger.assert(this.type == IFDType.ASCII, "Invalid type")

		let str = ""
		for(let i = 0; i < this.count; i++) {
			str += String.fromCharCode(view.getUint8(this.value + i))
		}
		this.metadata = JSON.parse(str) as object
	}
}

enum IFDTag {
	NewSubfileType				= 254,
	SubfileType					= 255,
	ImageWidth					= 256,
	ImageLength					= 257,
	BitsPerSample				= 258,
	Compression					= 259,
	PhotometricInterpretation	= 262,
	Threshholding				= 263,
	CellWidth					= 264,
	CellLength					= 265,
	FillOrder					= 266,
	ImageDescription			= 270,
	StripOffsets				= 273,
	Orientation					= 274,
	SamplesPerPixel				= 277,
	RowsPerStrip				= 278,
	StripByteCounts				= 279,
	MinSampleValue				= 280,
	MaxSampleValue				= 281,
	XResolution					= 282,
	YResolution					= 283,
	PlanarConfiguration			= 284,
	PageName					= 285,
	XPosition					= 286,
	YPosition					= 287,
	FreeOffsets					= 288,
	FreeByteCounts				= 289,
	GrayResponseUnit			= 290,
	GrayResponseCurve			= 291,
	ResolutionUnit				= 296,
	TransferFunction			= 301,
	Software					= 305,
	DateTime					= 306,
	Predictor					= 317,
	WhitePoint					= 318,
	PrimaryChromaticities		= 319,
	ColorMap					= 320,
	HalftoneHints				= 321,
	TileWidth					= 322,
	TileLength					= 323,
	TileOffsets					= 324,
	TileByteCounts				= 325,

	ModelPixelScale				= 33550,
	ModelTiePoint				= 33922,
	ModelTransformation			= 34744,
	GeoKeyDirectory				= 34735,
	GeoDoubleParams				= 34736,
	GeoAsciiParams				= 34737,
	GdalMetadata				= 42112,
	GdalNoData					= 42113,

	EarthBrowserMetadata		= 50505,
}

enum IFDType {
	BYTE			= 1,
	ASCII			= 2,
	SHORT			= 3,
	LONG			= 4,
	RATIONAL		= 5,
	SBYTE			= 6,
	UNDEFINED		= 7,
	SSHORT			= 8,
	SLONG			= 9,
	SRATIONAL		= 10,
	FLOAT			= 11,
	DOUBLE			= 12
}

function ifdTypeSize(type: IFDType): number {
	switch(type) {
		case IFDType.BYTE:
		case IFDType.ASCII:
		case IFDType.SBYTE:
		case IFDType.UNDEFINED:
			return 1
		case IFDType.SHORT:
		case IFDType.SSHORT:
			return 2
		case IFDType.LONG:
		case IFDType.SLONG:
		case IFDType.FLOAT:
			return 4
		case IFDType.RATIONAL:
		case IFDType.SRATIONAL:
		case IFDType.DOUBLE:
			return 8
		default:
			throw new Error("Invalid type")
	}
}
